import { DropdownButtonType, List, ListItem, SingleSelectDropdown, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { Messages } from '../../../../../codegen/Localize';
import { ITimeZone } from '../../../../../userSettings/serverApi/types/ITimeZone';
import { IGetTimeZonesResult, TimeZones } from '../../../../../userSettings/services/TimeZones';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { SettingsStyles } from '../../../../styles/CommonStyles';
import { dropdownListStyle, learnMoreStyle } from '../../../../styles/CommonStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';


export const METRICS_CONTENT_TYPE_FIELD = 'settings.metricsContentTypeEnabled';
export const METRICS_CONTENT_TYPE_SNAPSHOTTING_FIELD = 'settings.metricsContentTypeSnapshottingEnabled';
export const METRICS_SNAPSHOTTING_TIME_ZONE_LEARN_MORE_TEST_ID = 'metrics-snapshotting-time-zone-learn-more';
export const METRICS_SNAPSHOTTING_ENABLED_TEXT_LINK_TEST_ID = 'metrics-content-type-snapshotting-text-link';
export const METRICS_SNAPSHOTTING_ENABLED_CHECKBOX_TEST_ID = 'metrics-content-type-snapshotting-enabled';
export const METRICS_SNAPSHOTTING_TIME_ZONE_DROPDOWN_TEST_ID = 'default-metrics-snapshotting-time-zone-site-setting';
export const METRICS_SNAPSHOTTING_TIME_ZONE_TEXT_LINK_TEST_ID = 'metrics-snapshotting-time-zone-text-link';
export const unspecifiedMetricsSnapshottingTimeZone = 'UnspecifiedTimeZone';

export type MetricsContentTypeSectionProps = SiteSettingsForSiteAdminProps<'metricsContentTypeEnabled' | 'metricsContentTypeSnapshottingEnabled' | 'metricsSnapshottingTimeZone' | 'useDefaultMetricsSnapshottingTimeZone'>;

export function MetricsContentTypeSection(props: MetricsContentTypeSectionProps) {
  const metricsEnabledOnServer = getSessionManagementServiceInstance().isMetricsEnabledOnServer();
  const formik = useFormikContext<MetricsContentTypeSectionProps>();
  const changeTimeZoneHandler = useChangeTimeZoneHandler(formik);
  const timeZonesListItems = useTimeZonesListItems(metricsEnabledOnServer && FeatureFlags.metricsSnapshottingEnabled);
  return FeatureFlags.metricsContentTypeSiteSettingEnabled && metricsEnabledOnServer
  ? (
    <SettingsSection
      title={Messages.metricsContentTypeSiteSetting()}
      description={Messages.metricsContentTypeSiteSettingDescription()}
      testId={'metrics-content-type-setting-section'}>
      <div>
        <FormikCheckbox
          name={METRICS_CONTENT_TYPE_FIELD}
          label={Messages.metricsContentTypeSiteSettingEnable()}
          testId={'metrics-content-type-enabled'}>
          <MetricsSnapshottingSettings
            changeTimeZoneHandler={changeTimeZoneHandler}
            settingsValues={formik.values}
            timeZonesListItems={timeZonesListItems}
            metricsSnapshottingFFEnabled={FeatureFlags.metricsSnapshottingEnabled}/>
          </FormikCheckbox>

    </div>
    </SettingsSection>
    )
    : null;
}

/*
 * A custom component is needed to render the time zone options because of the custom mapping:
 *  1. blank option (UnspecifiedMetricsSnapshottingTimeZone) -> 
 *    useDefaultMetricsSnapshottingTimeZone=true, metricsSnapshottingTimeZone=undefined
 *  2. any other option -> useDefaultMetricsSnapshottingTimeZone=false, metricsSnapshottingTimeZone=that option's timeZoneId
 */
function useChangeTimeZoneHandler(formik: FormikContextType<MetricsContentTypeSectionProps>) {
  return React.useCallback(
    (selectedTimeZone: string) => {
      if (selectedTimeZone === unspecifiedMetricsSnapshottingTimeZone) {
        formik.setFieldValue('settings.useDefaultMetricsSnapshottingTimeZone', true);
        formik.setFieldValue('settings.metricsSnapshottingTimeZone', undefined);
      } else {
        formik.setFieldValue('settings.useDefaultMetricsSnapshottingTimeZone', false);
        formik.setFieldValue('settings.metricsSnapshottingTimeZone', selectedTimeZone);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue]);
}

function useTimeZonesListItems(metricsSnapshottingFFEnabled: boolean) {
  const [timeZonesListItems, setTimeZonesListItems] = React.useState<ListItem[]>([]);

  // API call to get the list of time zones
  React.useEffect(() => {
    if (metricsSnapshottingFFEnabled) {
      let shouldSetState = true; // will be false when component unmounts to prevent memory leak
      TimeZones.getAll().then((result: IGetTimeZonesResult) => {
        // adding a blank option for when the site is using the default time zone
        const blankOption: ITimeZone = {
          displayName: ' ',
          timeZoneId: unspecifiedMetricsSnapshottingTimeZone,
          timeZoneRawOffsetMinutes: 0
        };
        if (shouldSetState) {
          // Hyper does not support extra time zones, so we use only curated time zones.
          setTimeZonesListItems(
            [blankOption].concat(result.curatedTimeZonesList).map((timeZone: ITimeZone) => ({
              content: timeZone.displayName,
              value: timeZone.timeZoneId,
              testId: timeZone.timeZoneId
            }))
          );
        }
      });

      return () => {
        shouldSetState = false;
      };
    }
  }, [metricsSnapshottingFFEnabled]);

  return timeZonesListItems;
}

type MetricsSnapshottingSettingsProps = {
changeTimeZoneHandler: (selectedTimeZone: string) => void;
settingsValues: MetricsContentTypeSectionProps;
timeZonesListItems: ListItem[];
metricsSnapshottingFFEnabled: boolean;
};

function MetricsSnapshottingSettings(props: MetricsSnapshottingSettingsProps) {
const currentTimeZone = props.settingsValues.settings.useDefaultMetricsSnapshottingTimeZone ?
unspecifiedMetricsSnapshottingTimeZone : props.settingsValues.settings.metricsSnapshottingTimeZone;
  return props.metricsSnapshottingFFEnabled ? <>
  <div>
    {Messages.metricsContentTypeSnapshottingSiteSettingDescription()}
    <TextLinkWidget
      testId={METRICS_SNAPSHOTTING_ENABLED_TEXT_LINK_TEST_ID}
      className={learnMoreStyle}
      kind={TextLinkKind.Standard}
      href={HelpService.getHelpUrlByCategory('metrics-content-type-snapshotting')}
      target={'_blank'}
      rel={'noreferrer noopener'}>
    {Messages.learnMore()}
    </TextLinkWidget>
  </div>
  <div className={SettingsStyles.checkboxMessageIndent}>
    <FormikCheckbox
      name={METRICS_CONTENT_TYPE_SNAPSHOTTING_FIELD}
      label={Messages.metricsContentTypeSnapshottingSiteSettingEnable()}
      testId={METRICS_SNAPSHOTTING_ENABLED_CHECKBOX_TEST_ID}/>
  </div>
  <div>
    {Messages.defaultMetricsSnapshottingTimeZoneDropdownText()}
    <TextLinkWidget
      testId={METRICS_SNAPSHOTTING_TIME_ZONE_TEXT_LINK_TEST_ID}
      className={learnMoreStyle}
      kind={TextLinkKind.Standard}
      href={HelpService.getHelpUrlByCategory('metrics-snapshotting-time-zone')}
      target={'_blank'}
      rel={'noreferrer noopener'}>
    {Messages.learnMore()}
    </TextLinkWidget>
  </div>
  <SingleSelectDropdown
    list={<List items={props.timeZonesListItems} className={dropdownListStyle} />}
    value={currentTimeZone}
    onChange={props.changeTimeZoneHandler}
    type={DropdownButtonType.Outline}
    label=''
    testId={METRICS_SNAPSHOTTING_TIME_ZONE_DROPDOWN_TEST_ID}
  />
  </> : null;
}

export const MetricsContentTypeSpecs = componentRegistryEntry({
  name: 'MetricsContentTypeSection',
  component: MetricsContentTypeSection
});

